import type { CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { APPROACH_MS, CARRY_MS, FOLD_MS, IMPACT_MS } from './timing'
import type { DeliveryStage, Origin } from './useLetterDelivery'
import { Dog } from './Dog'
import { Letter } from './Letter'
import { PawPrints } from './PawPrints'

interface LetterCourierProps {
  stage: DeliveryStage
  origin: Origin | null
}

/**
 * How far right of the sprite's anchor point his mouth sits, in pixels.
 *
 * The letter in his jaws is drawn around x=34 of the 351-wide artwork, and the
 * sprite is 192px wide and centred on the anchor - so the mouth lands about
 * 96 - (34/351 * 192) = 77px left of it. Standing that far right of the letter
 * puts his mouth on it.
 */
const MOUTH_OFFSET = 77

/** Half the rendered sprite width, since it is centred on its anchor. */
const SPRITE_HALF = 96

/** Keeps him a little clear of the viewport edge when deciding if he fits. */
const EDGE_MARGIN = 8

/**
 * Where the dog stands, as x offsets from the card's centre.
 *
 * On a wide screen he waits beside the card and shoulders its edge, so the
 * offsets are relative to `halfWidth`. That only works while the viewport is
 * enough wider than the card to hold him: on a phone the card is nearly the
 * full width, and offsets past its edge put him completely off-screen - the
 * barge then happened where nobody could see it, and all you saw was the card
 * shaking by itself.
 *
 * So when he does not fit beside it, he comes in *over* the card instead,
 * positioned as a fraction of its width. The lunge stays a similar distance
 * either way, so the barge reads the same.
 *
 * `pickup` is unaffected by any of this: the card shrinks into a letter at its
 * own centre, so how far he must come to take it has nothing to do with how
 * wide the card was.
 */
function positions(halfWidth: number, viewportWidth: number) {
  const beside = {
    beside: halfWidth + 96,
    nudge: halfWidth + 34,
    bounce: halfWidth + 132,
    pickup: MOUTH_OFFSET,
  }

  // The card is centred, so his furthest position has to leave room for half a
  // sprite beyond it.
  const fitsBeside = viewportWidth / 2 >= beside.bounce + SPRITE_HALF + EDGE_MARGIN
  if (fitsBeside) return beside

  return {
    beside: halfWidth * 0.58,
    nudge: halfWidth * 0.3,
    bounce: halfWidth * 0.7,
    pickup: MOUTH_OFFSET,
  }
}

/**
 * The overlay half of the submit sequence: the dog, the letter he comes for,
 * and the prints he leaves.
 *
 * Everything is anchored to `origin`, the middle of the card the guest just
 * filled in, and the dog's resting positions are derived from the card's
 * measured width so he stops against its edge at any viewport size. Those go to
 * CSS as custom properties; the keyframes in animations.css do the rest.
 *
 * Rendered through a portal, which is load-bearing rather than tidiness.
 * `origin` is in viewport coordinates, so the overlay has to be positioned
 * against the viewport - but any ancestor with a transform becomes the
 * containing block for `position: fixed` descendants, and then `fixed inset-0`
 * silently anchors to that ancestor. An animated ancestor is enough to do it: a
 * keyframe ending in `transform: none` still computes to the identity matrix
 * while the animation's fill applies, which is not `none`. The card sits inside
 * exactly such a wrapper, so without the portal the dog runs past off-screen.
 * Keep new viewport-anchored overlays in a portal for the same reason.
 */
export function LetterCourier({ stage, origin }: LetterCourierProps) {
  const visible = stage === 'approach' || stage === 'impact' || stage === 'folding' || stage === 'carrying'
  if (!visible || !origin) return null

  const pos = positions(origin.halfWidth, window.innerWidth)

  // Inherited by everything below, including inside the SVGs.
  const vars = {
    '--approach-duration': `${APPROACH_MS}ms`,
    '--impact-duration': `${IMPACT_MS}ms`,
    '--fold-duration': `${FOLD_MS}ms`,
    '--carry-duration': `${CARRY_MS}ms`,
    '--dog-beside': `${pos.beside}px`,
    '--dog-nudge': `${pos.nudge}px`,
    '--dog-bounce': `${pos.bounce}px`,
    '--dog-pickup': `${pos.pickup}px`,
  } as CSSProperties

  // One class per stage, so the animations cannot overlap. `folding` is a
  // static position rather than an animation: he simply waits there.
  const dogMotion =
    stage === 'approach'
      ? 'animate-dog-approach'
      : stage === 'impact'
        ? 'animate-dog-impact'
        : stage === 'folding'
          ? 'dog-waiting'
          : 'animate-dog-carry'

  // He is only actually travelling during the approach and the exit.
  const onTheMove = stage === 'approach' || stage === 'carrying'

  return createPortal(
    <div
      className="pointer-events-none fixed inset-0 z-40 overflow-hidden"
      style={vars}
      aria-hidden="true"
    >
      {/* The letter, from the moment the card starts shrinking into it. */}
      {(stage === 'folding' || stage === 'carrying') && (
        <div
          className={`absolute ${stage === 'carrying' ? 'animate-letter-handoff' : ''}`}
          style={{ left: origin.x, top: origin.y }}
        >
          <Letter className="animate-letter-settle h-16 w-24 -translate-x-1/2 -translate-y-1/2 drop-shadow-md" />
        </div>
      )}

      {/* Prints, on the way in and on the way out. The first trail stays
          mounted past its own stage so it can finish fading rather than
          blinking out when the stage changes. */}
      {stage !== 'carrying' && (
        <div className="absolute" style={{ left: origin.x, top: origin.y + 46 }}>
          <PawPrints
            from={window.innerWidth * 0.75}
            to={pos.beside}
            travelMs={APPROACH_MS}
            count={7}
          />
        </div>
      )}
      {stage === 'carrying' && (
        <div className="absolute" style={{ left: origin.x, top: origin.y + 46 }}>
          <PawPrints
            from={pos.pickup}
            to={window.innerWidth * -0.75}
            travelMs={CARRY_MS}
            // He does not set off until the pick-up beat is over.
            startAt={0.44}
            count={9}
          />
        </div>
      )}

      {/* The dog, travelling along the line the card sits on. */}
      <div className="absolute" style={{ left: origin.x, top: origin.y }}>
        <div className={dogMotion}>
          <div className={onTheMove ? 'animate-dog-bob' : 'dog-standing'}>
            {/* Sized to the artwork's 351:292 aspect so it is not letterboxed. */}
            <Dog
              {...(stage === 'carrying' ? { letterClassName: 'animate-letter-carried' } : {})}
              className="h-40 w-48 -translate-x-1/2 -translate-y-1/2 drop-shadow-lg"
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
